// /cabinet/edu-report?child=<uuid> — отчёт по подбору.
//
// Показывает СНИМОК, а не свежий расчёт: справочник обновится, а документ,
// который семья распечатала, меняться задним числом не должен. Кнопка
// «Пересчитать» создаёт новый снимок, старый остаётся.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { loginRedirect } from "@/lib/return-to";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { generateEduReport, getEduReport } from "@/lib/edu-report.functions";
import { getEduAvailability } from "@/lib/edu-availability";
import { EduReport } from "@/components/edu/EduReport";
import { EduAccessError } from "@/components/edu/EduAccessError";
import { EduClosedNotice } from "@/components/edu/EduClosedNotice";
import { usePartnerAction } from "@/components/edu/PartnerApply";
import { useLang } from "@/lib/landing-i18n";

export const Route = createFileRoute("/cabinet_/edu-report")({
  validateSearch: (search: Record<string, unknown>): { child?: string } => ({
    child: typeof search.child === "string" ? search.child : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Образование за рубежом — отчёт" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EduReportPage,
});

function EduReportPage() {
  const { child: childId } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const fetchReport = useServerFn(getEduReport);
  const generate = useServerFn(generateEduReport);
  const checkOpen = useServerFn(getEduAvailability);

  const [authChecked, setAuthChecked] = useState(false);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUserEmail(data.session?.user.email ?? undefined);
      if (!data.session) {
        // Язык и путь возврата едут на вход: после письма человек вернётся сюда.
        navigate(loginRedirect());
        return;
      }
      setAuthChecked(true);
    });
  }, [navigate]);

  // Модуль закрыт, пока справочник стран не опубликован: см. edu-availability.
  const moduleOpen = useQuery({
    queryKey: ["edu-open"],
    enabled: authChecked,
    queryFn: () => checkOpen({}),
  });
  const isOpen = moduleOpen.data?.open === true;

  const state = useQuery({
    queryKey: ["edu-report", childId],
    enabled: Boolean(authChecked && isOpen && childId),
    queryFn: () => fetchReport({ data: { childId: childId as string } }),
  });

  // Кнопка заявки у партнёрского вуза: почта входа подставляется в форму.
  const { lang } = useLang();
  const partnerAction = usePartnerAction({ lang, context: "cabinet", defaultEmail: userEmail });

  const recalc = useMutation({
    mutationFn: () => generate({ data: { childId: childId as string } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["edu-report", childId] }),
  });

  if (!childId) {
    return (
      <div className="mx-auto max-w-2xl p-6 text-sm text-muted-foreground">
        Не указан ребёнок. Откройте отчёт из кабинета.
      </div>
    );
  }
  if (!authChecked || moduleOpen.isLoading) {
    return <div className="mx-auto max-w-2xl p-6 text-sm text-muted-foreground">Загрузка…</div>;
  }
  if (!isOpen) return <EduClosedNotice />;
  if (state.isLoading) {
    return <div className="mx-auto max-w-2xl p-6 text-sm text-muted-foreground">Загрузка…</div>;
  }
  {
    /* ОШИБКА — НЕ ПУСТОЕ СОСТОЯНИЕ, И ЭТО НЕ ПРИДИРКА.
       Отчёт чужого ребёнка рисовал «Сначала нужно заполнить анкету»: отказ был
       неотличим от пустоты, и экран отвечал на вопрос, отвечать на который не
       имел права. Отдельная ветка — раньше всех остальных, потому что при
       ошибке `state.data` пуст, и любая проверка ниже читает пустоту как факт. */
  }
  if (state.isError) {
    return <EduAccessError error={state.error} childId={childId} />;
  }
  if (state.data?.tableMissing) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900">
          Таблицы модуля ещё не созданы — миграции накатываются вручную.
        </div>
      </div>
    );
  }

  const report = state.data?.report;

  if (!report) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 p-6">
        <p className="text-sm text-muted-foreground">
          {state.data?.hasProfile
            ? "Анкета заполнена, но расчёт ещё не делался."
            : "Сначала нужно заполнить анкету."}
        </p>
        {state.data?.hasProfile ? (
          <button
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            onClick={() => recalc.mutate()}
            disabled={recalc.isPending}
          >
            {recalc.isPending ? "Считаю…" : "Посчитать варианты"}
          </button>
        ) : (
          <a
            className="inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
            href={`/cabinet/edu?child=${childId}`}
          >
            Заполнить анкету
          </a>
        )}
      </div>
    );
  }

  return (
    <EduReport
      result={report.result}
      texts={report.texts}
      version={report.version}
      createdAt={report.created_at}
      consultHref={`/cabinet/edu-consult?child=${childId}`}
      retakeHref={`/cabinet/edu?child=${childId}&again=1`}
      onRecalculate={() => recalc.mutate()}
      recalculating={recalc.isPending}
      institutionAction={partnerAction}
    />
  );
}
